import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { startAutoFlush } from "./lib/offline.js";
import { RequireAuth } from "./components/Guards.js";
import {
  Landing,
  RootRedirect,
  Dashboard,
  CaptureVideo,
  CaptureImage,
  CaptureScreen,
  Processing,
  Picker,
  Timeline,
  Annotate,
  Preview,
  Digest,
  Viewer,
  Handsfree,
  Qr,
  Library,
  BookView,
  Reader,
  AiReview,
  Multilingual,
  Studio,
  Versions,
  Approval,
  Analytics,
  Members,
  StoresAdmin,
  Settings,
  Billing,
  Ops,
  Inbox,
  Learning,
  Feedback,
  Auth,
  Onboarding,
  Checklist,
} from "./screens/index.js";

/** Surface hook kept here for backward-compat (also exported from lib/useSurface). */
export { useSurface } from "./lib/useSurface.js";

export function App() {
  // PWA offline queue: flush captured media whenever connectivity returns.
  useEffect(() => startAutoFlush(), []);

  return (
    <Routes>
      {/* entry gate: routes by session + role (no UI) */}
      <Route path="/" element={<RootRedirect />} />
      {/* dev-only design-system index (production hides it behind /) */}
      <Route path="/dev" element={import.meta.env.DEV ? <Landing /> : <Navigate to="/" replace />} />

      {/* public */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/verify" element={<Auth />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/qr" element={<Qr />} />

      {/* authenticated app */}
      <Route element={<RequireAuth />}>
        {/* console (light) — management */}
        <Route path="/dashboard" element={<Dashboard />} />

        {/* field (dark) — creator / capture */}
        <Route path="/capture" element={<Navigate to="/capture/video" replace />} />
        <Route path="/capture/video" element={<CaptureVideo />} />
        <Route path="/capture/image" element={<CaptureImage />} />
        <Route path="/capture/screen" element={<CaptureScreen />} />
        <Route path="/processing/:id" element={<Processing />} />
        <Route path="/picker/:id" element={<Picker />} />
        <Route path="/timeline/:id" element={<Timeline />} />
        <Route path="/annotate" element={<Annotate />} />
        <Route path="/annotate/:id" element={<Annotate />} />
        <Route path="/viewer/:id" element={<Viewer />} />
        <Route path="/read/:id" element={<Reader />} />
        <Route path="/digest/:id" element={<Digest />} />
        <Route path="/handsfree/:id" element={<Handsfree />} />
        <Route path="/checklist/:id" element={<Checklist />} />

        {/* console (light) — editing / library / learning */}
        <Route path="/preview/:id" element={<Preview />} />
        <Route path="/library" element={<Library />} />
        <Route path="/book/:id" element={<BookView />} />
        <Route path="/ai-review" element={<AiReview />} />
        <Route path="/multilingual" element={<Multilingual />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/versions" element={<Versions />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/learning" element={<Learning />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/inbox" element={<Inbox />} />

        {/* role-gated areas: navigable by any signed-in user (UI-only); the server enforces
            real permissions on mutations (approve / member & settings changes → 403). */}
        <Route path="/approval" element={<Approval />} />
        <Route path="/members" element={<Members />} />
        <Route path="/stores" element={<StoresAdmin />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/ops" element={<Ops />} />
        <Route path="/billing" element={<Billing />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
